/**
 * P2-2: Suspension Enforcement UI Components
 * Visual components for showing suspension status and blocking actions
 */
import React from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Ban, Phone, Mail, ShieldX, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

/**
 * Global Suspension Banner
 * Shows at the top of clinic admin dashboard when account is suspended
 */
export const SuspensionBanner = ({ suspensionDetails, onContactSupport }) => {
  if (!suspensionDetails) return null;

  const suspendedDate = suspensionDetails.suspendedAt 
    ? formatDateLocale(suspensionDetails.suspendedAt) 
    : 'Unknown';

  return (
    <div className="bg-red-600 text-white px-4 py-3 shadow-lg">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldX className="h-6 w-6" />
          <div>
            <p className="font-bold text-lg">Account Suspended</p>
            <p className="text-sm text-red-100">
              Your account was suspended on {suspendedDate}. Most actions are disabled.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm" 
            className="bg-white text-red-600 hover:bg-red-50 border-white"
            onClick={onContactSupport}
          >
            <Phone className="h-4 w-4 mr-2" />
            Contact Support
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Suspension Reason Card
 * Shows detailed suspension information
 */
export const SuspensionReasonCard = ({ suspensionDetails }) => {
  if (!suspensionDetails) return null;

  return (
    <Card className="border-red-200 bg-red-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2 text-red-700">
          <AlertTriangle className="h-5 w-5" />
          Account Suspension Notice
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="text-sm font-medium text-red-800">Reason:</p>
          <p className="text-sm text-red-700">{suspensionDetails.suspensionReason || 'No reason provided'}</p>
        </div>
        <div className="flex items-center gap-4 text-xs text-red-600">
          <span>Suspended: {formatDateLocale(suspensionDetails.suspendedAt)}</span>
          {suspensionDetails.suspendedBy && (
            <span>By: {suspensionDetails.suspendedBy}</span>
          )}
        </div>
        <div className="pt-2 border-t border-red-200">
          <p className="text-sm text-red-700">
            To reinstate your account, please contact TreatX support at{' '}
            <a href="mailto:support@treatx.health" className="underline font-medium">
              support@treatx.health
            </a>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Disabled Action Button
 * Wraps a button and shows tooltip explaining why it's disabled
 */
export const DisabledSuspendedButton = ({ children, tooltipText = 'Action disabled due to suspension' }) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-block">
            <Button disabled className="opacity-50 cursor-not-allowed">
              <Ban className="h-4 w-4 mr-2" />
              {children}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent className="bg-red-600 text-white">
          <p className="text-sm">{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Suspension Status Badge
 * Small badge showing suspension status
 */
export const SuspensionBadge = ({ isSuspended }) => {
  if (!isSuspended) {
    return (
      <Badge className="bg-green-100 text-green-700">
        <CheckCircle className="h-3 w-3 mr-1" />
        Active
      </Badge>
    );
  }

  return (
    <Badge className="bg-red-100 text-red-700">
      <ShieldX className="h-3 w-3 mr-1" />
      Suspended
    </Badge>
  );
};

/**
 * Clinic Unavailable Modal
 * Shows when a patient tries to interact with a suspended clinic
 */
export const ClinicUnavailableModal = ({ isOpen, onClose, clinicName }) => {
  const navigate = useNavigate();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Provider Temporarily Unavailable
          </DialogTitle>
          <DialogDescription>
            {clinicName ? `${clinicName} is` : 'This provider is'} temporarily unavailable on TreatX.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-gray-600 mb-4">
            Please select another provider or contact TreatX support for assistance.
          </p>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Mail className="h-4 w-4" />
            <a href="mailto:support@treatx.health" className="text-blue-600 hover:underline">
              support@treatx.health
            </a>
          </div>
        </div>
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => { onClose(); navigate('/patient/clinic-search'); }}>
            Find Another Provider
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Sales Rep Suspension Banner
 * Inline banner for sales rep dashboard
 */
export const SalesRepSuspensionBanner = () => {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
        <div>
          <p className="font-medium text-amber-800">Provider Suspended</p>
          <p className="text-sm text-amber-700">
            Actions are temporarily disabled. You can view patient history in read-only mode.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Read-Only Mode Indicator
 * Shows when viewing data in read-only mode due to suspension
 */
export const ReadOnlyIndicator = () => {
  return (
    <div className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
      <Ban className="h-3 w-3" />
      Read-only
    </div>
  );
};

export default {
  SuspensionBanner,
  SuspensionReasonCard,
  DisabledSuspendedButton,
  SuspensionBadge,
  ClinicUnavailableModal,
  SalesRepSuspensionBanner,
  ReadOnlyIndicator
};
